/**
 * Table designer (ADR-0010 P4.2) — the standalone ROUTE shell for one Data
 * Table. The actual schema editor (grid + Advanced drawer + dialogs) lives in
 * the reusable `TableDesigner` component, shared with the in-editor `AppTablesPane`
 * (the single-IDE "Dati" rail area, ADR-0048) so there is ONE designer.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { appsApi } from "@/lib/api-apps";
import { usePermissions } from "@/lib/permissions";
import { TableDesigner } from "@/components/apps/TableDesigner";

export function TableDesignerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appId = "", tableId = "" } = useParams<{ appId: string; tableId: string }>();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");

  // Header chrome only — the body fetches the same `["apps",appId,"tables",tableId]`
  // query (TanStack dedupes), so we read its name/description for the page title.
  const tableQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId],
    queryFn: () => appsApi.getTable(appId, tableId),
    enabled: !!appId && !!tableId,
  });

  return (
    <PageContainer>
      <PageHeader
        title={tableQ.data?.name ?? t("apps.loading", "Loading…")}
        description={tableQ.data?.description ?? t("apps.tableDesignerSubtitle", "Data & fields")}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate(`/apps/${appId}`)}>
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.backToApp", "Back to app")}
          </Button>
        }
      />
      <PageContent>
        {tableQ.isError ? (
          <Alert tone="danger" title={t("apps.loadTableFailed", "Could not load table")}>
            <span className="font-mono text-xs">{(tableQ.error as Error).message}</span>
          </Alert>
        ) : (
          <TableDesigner appId={appId} tableId={tableId} canWrite={canWrite} />
        )}
      </PageContent>
    </PageContainer>
  );
}
